/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import {
 AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
 Tooltip, ResponsiveContainer, Legend, LineChart, Line,
} from 'recharts';
const api = async (sti, metode = 'GET', data = null) => {
 const res = await fetch('/api' + sti, {
 method: metode,
 credentials: 'include',
 headers: {
 'Content-Type': 'application/json',
 },
 body: data ? JSON.stringify(data) : null,
 });
 if (!res.ok) {
 const err = await res.json().catch(() => ({}));
 throw new Error(err.fejl || `HTTP ${res.status}`);
 }
 return res.json();
};
// ─── Design tokens ─────────────────────────────────────────────────────────
const C = {
 bg: '#060917',
 kort: 'rgba(8,12,30,0.60)',
 kant: 'rgba(255,255,255,0.08)',
 tekst: '#e2e8f0',
 subtekst: '#7a8fa6',
 ok: '#22c55e',
 advarsel: '#f59e0b',
 kritisk: '#ef4444',
 ukendt: '#94a3b8',
 info: '#3b82f6',
 lilla: '#8b5cf6',
 sidebar: '#060d1a',
 primary: '#2322be',
 accent: '#3fd4c7',
};
const DIVISION_IKONER = {
 1: '', 2: '', 3: '', 4: '', 5: '',
 6: '', 7: '', 8: '', 9: '', 10: '',
 11: '', 12: '', 13: '',
};
function statusFarve(s) {
 if (s === 'ok') return C.ok;
 if (s === 'advarsel') return C.advarsel;
 if (s === 'kritisk') return C.kritisk;
 return C.ukendt;
}
function statusBaggrund(s) {
 if (s === 'ok') return 'rgba(34,197,94,0.10)';
 if (s === 'advarsel') return 'rgba(245,158,11,0.10)';
 if (s === 'kritisk') return 'rgba(239,68,68,0.10)';
 return 'rgba(8,12,30,0.40)';
}
// ─── Genbrugelige komponenter ───────────────────────────────────────────────
function Kort({ children, style = {} }) {
 return (
 <div style={{
 background: 'rgba(8,12,30,0.60)', border: '1px solid rgba(255,255,255,0.08)',
 borderRadius: 12, padding: 20,
 backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
 ...style,
 }}>
 {children}
 </div>
 );
}
function StatusDot({ status, stor = false }) {
 return (
 <span style={{
 display: 'inline-block',
 width: stor ? 12 : 8,
 height: stor ? 12 : 8,
 borderRadius: '50%',
 background: statusFarve(status),
 boxShadow: status !== 'ukendt' ? `0 0 6px ${statusFarve(status)}` : 'none',
 flexShrink: 0,
 }} />
 );
}
function KpiBar({ kpi }) {
 const pct = kpi.maal > 0 ? Math.min(100, Math.round((kpi.vaerdi / kpi.maal) * 100)) : 0;
 const lavere_er_bedre = ['fejlrate','churn','responstid','cpl','brud','tvister','uautoriserede','advarsel','nedbrud','databrud']
 .some(k => (kpi.kpi_navn || '').toLowerCase().includes(k));
 const barFarve = kpi.status === 'ok' ? C.ok :
 kpi.status === 'advarsel' ? C.advarsel :
 kpi.status === 'kritisk' ? C.kritisk : '#cbd5e1';
 return (
 <div style={{ marginBottom: 10 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}> <span style={{ fontSize: 12, color: C.subtekst, flex: 1 }}>{kpi.kpi_navn}</span> <span style={{ fontSize: 12, fontWeight: 700, color: barFarve, marginLeft: 8 }}>
 {kpi.vaerdi != null ? `${kpi.vaerdi}${kpi.enhed ? ' ' + kpi.enhed : ''}` : '—'}
 </span>
 {kpi.maal != null && (
 <span style={{ fontSize: 11, color: C.subtekst, marginLeft: 6 }}>/ {kpi.maal}</span>
 )}
 </div>
 {kpi.maal > 0 && (
 <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}> <div style={{
 height: '100%',
 width: `${lavere_er_bedre ? Math.min(100, pct) : pct}%`,
 background: barFarve,
 borderRadius: 2,
 transition: 'width .5s ease',
 }} /> </div>
 )}
 </div>
 );
}
// ─── Metrik-kort øverst ──────────────────────────────────────────────────────
function MetrikOverblik({ metrikker }) {
 const vigtige = ['mrr', 'aktive_klienter', 'nye_leads_maaned', 'fb_gruppe_medlemmer',
 'api_oppetid', 'runway_maaneder', 'churn_rate', 'roas'];
 const vis = metrikker.filter(m => vigtige.includes(m.metrik_kode));
 const KILDE_BADGE = {
 live: { label: '● Live', color: C.ok, bg: 'rgba(34,197,94,0.15)' },
 manuel: { label: 'Manuel', color: C.advarsel, bg: 'rgba(245,158,11,0.15)' },
 ikke_tilsluttet: { label: null },
 };
 return (
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
 {vis.map(m => {
 const kilde = m.datakilde || 'ikke_tilsluttet';
 const harData = m.vaerdi != null;
 const dimmed = kilde === 'ikke_tilsluttet';
 const badge = KILDE_BADGE[kilde] || {};
 const pct = harData && m.maal > 0 ? (m.vaerdi / m.maal) * 100 : null;
 const lavere = ['churn', 'cpl', 'responstid', 'fejlrate'].some(k => m.metrik_kode.includes(k));
 let status = 'ukendt';
 if (pct !== null) {
 status = lavere
 ? (m.vaerdi <= m.maal ? 'ok' : m.vaerdi <= m.maal * 1.5 ? 'advarsel' : 'kritisk')
 : (pct >= 100 ? 'ok' : pct >= 70 ? 'advarsel' : 'kritisk');
 }
 return (
 <div key={m.metrik_kode} style={{
 background: dimmed ? 'rgba(8,12,30,0.30)' : 'rgba(8,12,30,0.60)',
 border: `1px solid ${dimmed ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)'}`,
 borderRadius: 10, padding: 14,
 backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
 borderTop: `3px solid ${dimmed ? 'rgba(255,255,255,0.06)' : statusFarve(status)}`,
 opacity: dimmed ? 0.55 : 1,
 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}> <div style={{ fontSize: 11, color: C.subtekst, fontWeight: 500, flex: 1, lineHeight: 1.3 }}>
 {m.metrik_navn}
 </div>
 {badge.label && (
 <span style={{
 fontSize: 10, fontWeight: 700, color: badge.color,
 background: badge.bg, padding: '1px 6px', borderRadius: 8,
 marginLeft: 4, flexShrink: 0,
 }}>{badge.label}</span>
 )}
 </div>
 {kilde === 'ikke_tilsluttet' ? (
 <div> <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Ikke tilsluttet</div> <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>Ingen integration konfigureret</div> </div>
 ) : !harData ? (
 <div> <div style={{ fontSize: 13, color: C.subtekst, marginTop: 4 }}>
 {kilde === 'live' ? ' Afventer data' : ' Ikke registreret'}
 </div> <div style={{ fontSize: 10, color: C.subtekst, marginTop: 4, lineHeight: 1.4 }}>
 {kilde === 'live' ? 'Scheduler opdaterer automatisk' : 'Opdater manuelt'}
 </div> </div>
 ) : (
 <> <div style={{ fontSize: 24, fontWeight: 800, color: C.tekst }}>
 {m.vaerdi.toLocaleString('da-DK')}
 </div> <div style={{ fontSize: 11, color: C.subtekst, marginTop: 2 }}>
 Mål: {m.maal != null ? m.maal.toLocaleString('da-DK') : '—'} {m.enhed}
 </div> </>
 )}
 </div>
 );
 })}
 </div>
 );
}
// ─── Leads pipeline ──────────────────────────────────────────────────────────
function LeadsPipeline({ leads }) {
 const kilder = [...new Set(leads.map(l => l.kilde))];
 const statusser = ['ny', 'kontaktet', 'møde', 'klient'];
 const statusNavne = { ny: 'Nye leads', kontaktet: 'Kontaktet', møde: 'Møde booket', klient: 'Aktiv klient' };
 const total_leads = leads.filter(l => l.status === 'ny').reduce((s, l) => s + (l.antal || 0), 0);
 const total_klient = leads.filter(l => l.status === 'klient').reduce((s, l) => s + (l.antal || 0), 0);
 return (
 <Kort style={{ marginBottom: 24 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}> <h3 style={{ margin: 0, fontSize: 16, color: C.tekst }}> Leads Pipeline — {new Date().toLocaleString('da-DK', { month: 'long', year: 'numeric' })}</h3> <div style={{ display: 'flex', gap: 12 }}> <span style={{ fontSize: 13, color: C.subtekst }}>Nye leads: <strong style={{ color: C.info }}>{total_leads}</strong></span> <span style={{ fontSize: 13, color: C.subtekst }}>Nye klienter: <strong style={{ color: C.ok }}>{total_klient}</strong></span> </div> </div> <div style={{ overflowX: 'auto' }}> <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}> <thead> <tr> <th style={{ textAlign: 'left', padding: '8px 12px', color: C.subtekst, fontWeight: 600, borderBottom: `1px solid ${C.kant}` }}>
 Kilde
 </th>
 {statusser.map(s => (
 <th key={s} style={{ textAlign: 'center', padding: '8px 12px', color: C.subtekst, fontWeight: 600, borderBottom: `1px solid ${C.kant}` }}>
 {statusNavne[s]}
 </th>
 ))}
 <th style={{ textAlign: 'center', padding: '8px 12px', color: C.subtekst, fontWeight: 600, borderBottom: `1px solid ${C.kant}` }}>
 Konv. %
 </th> </tr> </thead> <tbody>
 {kilder.map((kilde, i) => {
 const get = (st) => leads.find(l => l.kilde === kilde && l.status === st)?.antal || 0;
 const nye = get('ny');
 const konv = nye > 0 ? Math.round((get('klient') / nye) * 100) : 0;
 return (
 <tr key={kilde} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : C.kort }}> <td style={{ padding: '10px 12px', fontWeight: 600, color: C.tekst }}>{kilde}</td>
 {statusser.map(s => (
 <td key={s} style={{ padding: '10px 12px', textAlign: 'center' }}> <span style={{
 background: s === 'klient' ? 'rgba(34,197,94,0.10)' : 'rgba(255,255,255,0.03)',
 color: s === 'klient' ? C.ok : C.tekst,
 fontWeight: s === 'klient' ? 700 : 400,
 padding: '2px 10px', borderRadius: 20, fontSize: 13,
 }}>
 {get(s)}
 </span> </td>
 ))}
 <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: konv >= 10 ? C.ok : konv >= 5 ? C.advarsel : C.kritisk }}>
 {konv}%
 </td> </tr>
 );
 })}
 </tbody> </table> </div> </Kort>
 );
}
// ─── Division-oversigt ────────────────────────────────────────────────────────
function DivisionKort({ division, onClick, aktiv }) {
 const totalKpi = division.total_kpi_ok + division.total_kpi_advarsel + division.total_kpi_kritisk;
 const divStatus = division.total_kpi_kritisk > 0 ? 'kritisk'
 : division.total_kpi_advarsel > 0 ? 'advarsel'
 : totalKpi > 0 ? 'ok' : 'ukendt';
 return (
 <div
 onClick={onClick}
 style={{
 background: aktiv ? statusBaggrund(divStatus) : C.kort,
 border: `1px solid ${aktiv ? statusFarve(divStatus) : C.kant}`,
 borderRadius: 10,
 padding: 14,
 cursor: 'pointer',
 transition: 'all .15s',
 borderLeft: `4px solid ${statusFarve(divStatus)}`,
 }}
 onMouseEnter={e => { if (!aktiv) e.currentTarget.style.borderColor = statusFarve(divStatus); }}
 onMouseLeave={e => { if (!aktiv) e.currentTarget.style.borderColor = C.kant; }}
 > <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}> <span style={{ fontSize: 20 }}>{DIVISION_IKONER[division.nr]}</span> <div> <div style={{ fontSize: 11, color: C.subtekst, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>DIV {division.nr}</div> <div style={{ fontSize: 14, fontWeight: 700, color: C.tekst, lineHeight: 1.2 }}>{division.navn}</div> </div> </div> <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}> <span style={{ fontSize: 12, color: C.subtekst }}>
 {division.afdelinger.length} afdelinger
 </span>
 {division.total_kpi_ok > 0 && (
 <span style={{ fontSize: 12, color: C.ok, fontWeight: 600 }}> {division.total_kpi_ok}</span>
 )}
 {division.total_kpi_advarsel > 0 && (
 <span style={{ fontSize: 12, color: C.advarsel, fontWeight: 600 }}> {division.total_kpi_advarsel}</span>
 )}
 {division.total_kpi_kritisk > 0 && (
 <span style={{ fontSize: 12, color: C.kritisk, fontWeight: 600 }}>× {division.total_kpi_kritisk}</span>
 )}
 </div> </div>
 );
}
// ─── Afdeling-detail ──────────────────────────────────────────────────────────
function AfdelingDetail({ afdeling, onLuk, onRefresh }) {
 const [visPrompt, setVisPrompt] = useState(false);
 const [editKpi, setEditKpi] = useState(null);
 const [kpiVaerdi, setKpiVaerdi] = useState('');
 const [gemmer, setGemmer] = useState(false);
 const [kpiStatus, setKpiStatus] = useState(null); // 'ok' | 'fejl' | null
 const kpier = afdeling.kpi_data || [];
 const autonomt = Array.isArray(afdeling.autonomt) ? afdeling.autonomt : JSON.parse(afdeling.autonomt || '[]');
 const godkendelse = Array.isArray(afdeling.kræver_godkendelse) ? afdeling.kræver_godkendelse : JSON.parse(afdeling.kræver_godkendelse || '[]');
 const gemKpi = async () => {
 const val = parseFloat(kpiVaerdi);
 if (!editKpi || kpiVaerdi === '' || isNaN(val)) return;
 setGemmer(true);
 setKpiStatus(null);
 try {
 const res = await api('/os/firma/kpi', 'PATCH', {
 afdeling_kode: afdeling.kode,
 kpi_navn: editKpi,
 vaerdi: val,
 });
 if (res?.fejl) throw new Error(res.fejl);
 setEditKpi(null);
 setKpiVaerdi('');
 setKpiStatus('ok');
 onRefresh?.();
 setTimeout(() => setKpiStatus(null), 3000);
 } catch (err) {
 console.error('[FirmaOverblik] gemKpi fejl:', err);
 setKpiStatus('fejl');
 } finally {
 setGemmer(false);
 }
 };
 return (
 <div style={{
 position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
 display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
 }} onClick={(e) => { if (e.target === e.currentTarget) onLuk(); }}> <div style={{
 background: C.kort, borderRadius: 16, width: '100%', maxWidth: 680,
 maxHeight: '90vh', overflow: 'auto',
 boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
 }}>
 {/* Header */}
 <div style={{
 padding: '20px 24px',
 borderBottom: `1px solid ${C.kant}`,
 display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
 background: `linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)`,
 borderRadius: '16px 16px 0 0',
 }}> <div> <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
 {DIVISION_IKONER[afdeling.division_nr]} {afdeling.division_navn}
 </div> <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: 20 }}>{afdeling.navn}</h2> <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}> <StatusDot status={afdeling.status || 'ukendt'} stor /> <span style={{ fontSize: 12, color: '#94a3b8' }}>{afdeling.status || 'ukendt'}</span> </div> </div> <button onClick={onLuk} style={{
 background: 'rgba(255,255,255,0.1)', border: 'none', color: '#94a3b8',
 borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14,
 }}></button> </div> <div style={{ padding: 24 }}>
 {/* Beskrivelse */}
 <p style={{ color: C.subtekst, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
 {afdeling.beskrivelse}
 </p>
 {/* KPIer */}
 {kpier.length > 0 && (
 <div style={{ marginBottom: 20 }}> <h4 style={{ margin: '0 0 12px', fontSize: 14, color: C.tekst }}> KPI'er</h4>
 {kpier.map(k => (
 <div key={k.kpi_navn}>
 {editKpi === k.kpi_navn ? (
 <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}> <span style={{ fontSize: 12, color: C.subtekst, flex: 1 }}>{k.kpi_navn}</span> <input
 autoFocus
 type="number"
 value={kpiVaerdi}
 onChange={e => setKpiVaerdi(e.target.value)}
 style={{ width: 90, padding: '4px 8px', borderRadius: 6, border: `1px solid ${C.info}`, fontSize: 13 }}
 onKeyDown={e => { if (e.key === 'Enter') gemKpi(); if (e.key === 'Escape') setEditKpi(null); }}
 /> <span style={{ fontSize: 11, color: C.subtekst }}>{k.enhed}</span> <button onClick={gemKpi} disabled={gemmer} style={{
 background: C.ok, color: '#fff', border: 'none', borderRadius: 6,
 padding: '4px 10px', fontSize: 12, cursor: 'pointer',
 }}>{gemmer ? '...' : 'Gem'}</button> <button onClick={() => setEditKpi(null)} style={{
 background: '#e2e8f0', color: C.tekst, border: 'none', borderRadius: 6,
 padding: '4px 10px', fontSize: 12, cursor: 'pointer',
 }}></button> </div>
 ) : (
 <div
 style={{ cursor: 'pointer' }}
 onClick={() => { setEditKpi(k.kpi_navn); setKpiVaerdi(k.vaerdi || ''); }}
 title="Klik for at opdatere"
 > <KpiBar kpi={k} /> </div>
 )}
 </div>
 ))}
 {kpiStatus === 'ok' && (
 <div style={{ fontSize: 12, color: C.ok, marginTop: 6 }}> Gemt</div>
 )}
 {kpiStatus === 'fejl' && (
 <div style={{ fontSize: 12, color: C.kritisk, marginTop: 6 }}> Kunne ikke gemme — prøv igen</div>
 )}
 <div style={{ fontSize: 11, color: C.subtekst, marginTop: 4 }}>
 Klik på et KPI for at opdatere værdien
 </div> </div>
 )}
 {/* KPI definitioner (hvis ingen live data) */}
 {kpier.length === 0 && afdeling.kpi_definitioner && (
 <div style={{ marginBottom: 20 }}> <h4 style={{ margin: '0 0 12px', fontSize: 14, color: C.tekst }}> KPI-mål</h4>
 {(Array.isArray(afdeling.kpi_definitioner)
 ? afdeling.kpi_definitioner
 : JSON.parse(afdeling.kpi_definitioner || '[]')
 ).map((k, i) => (
 <div key={i} style={{
 display: 'flex', justifyContent: 'space-between',
 padding: '6px 0', borderBottom: `1px solid ${C.kant}`, fontSize: 13,
 }}> <span style={{ color: C.subtekst }}>{k.navn}</span> <span style={{ fontWeight: 600, color: C.info }}>Mål: {k.maal} {k.enhed}</span> </div>
 ))}
 </div>
 )}
 {/* Autonomt */}
 <div style={{ marginBottom: 16 }}> <h4 style={{ margin: '0 0 10px', fontSize: 14, color: C.tekst }}> Kan gøre autonomt</h4> <ul style={{ margin: 0, paddingLeft: 20 }}>
 {autonomt.map((item, i) => (
 <li key={i} style={{ fontSize: 13, color: C.subtekst, marginBottom: 4, lineHeight: 1.5 }}>{item}</li>
 ))}
 </ul> </div>
 {/* Kræver godkendelse */}
 <div style={{ marginBottom: 20 }}> <h4 style={{ margin: '0 0 10px', fontSize: 14, color: C.tekst }}> Kræver Renés godkendelse</h4> <ul style={{ margin: 0, paddingLeft: 20 }}>
 {godkendelse.map((item, i) => (
 <li key={i} style={{ fontSize: 13, color: C.advarsel, marginBottom: 4, lineHeight: 1.5 }}>{item}</li>
 ))}
 </ul> </div>
 {/* System prompt */}
 <div> <button
 onClick={() => setVisPrompt(!visPrompt)}
 style={{
 background: 'rgba(255,255,255,0.04)', border: 'none', borderRadius: 8,
 padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: C.tekst,
 width: '100%', textAlign: 'left', fontWeight: 600,
 }}
 >
 {visPrompt ? '▾' : '▸'} System-prompt (AI-instruktion)
 </button>
 {visPrompt && (
 <pre style={{
 background: '#0f172a', color: '#e2e8f0', padding: 16,
 borderRadius: '0 0 8px 8px', fontSize: 11, lineHeight: 1.6,
 whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
 maxHeight: 300, overflow: 'auto',
 }}>
 {afdeling.system_prompt}
 </pre>
 )}
 </div> </div> </div> </div>
 );
}
// ─── Division-sektion ─────────────────────────────────────────────────────────
function DivisionSektion({ division, aktivAfdeling, onVaelgAfdeling }) {
 const [aaben, setAaben] = useState(false);
 const divStatus = division.total_kpi_kritisk > 0 ? 'kritisk'
 : division.total_kpi_advarsel > 0 ? 'advarsel'
 : division.total_kpi_ok > 0 ? 'ok' : 'ukendt';
 return (
 <div style={{ marginBottom: 8 }}>
 {/* Division header */}
 <div
 onClick={() => setAaben(!aaben)}
 style={{
 display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
 background: aaben ? 'rgba(255,255,255,0.03)' : C.kort,
 border: `1px solid ${C.kant}`, borderRadius: aaben ? '10px 10px 0 0' : 10,
 cursor: 'pointer', userSelect: 'none',
 borderLeft: `4px solid ${statusFarve(divStatus)}`,
 }}
 > <span style={{ fontSize: 18 }}>{DIVISION_IKONER[division.nr]}</span> <div style={{ flex: 1 }}> <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}> <span style={{ fontSize: 11, color: C.subtekst, fontWeight: 600 }}>DIV {division.nr}</span> <span style={{ fontSize: 15, fontWeight: 700, color: C.tekst }}>{division.navn}</span> </div> <div style={{ display: 'flex', gap: 6, marginTop: 2 }}> <span style={{ fontSize: 12, color: C.subtekst }}>{division.afdelinger.length} afdelinger</span>
 {division.total_kpi_ok > 0 && <span style={{ fontSize: 12, color: C.ok }}> {division.total_kpi_ok} ok</span>}
 {division.total_kpi_advarsel > 0 && <span style={{ fontSize: 12, color: C.advarsel }}> {division.total_kpi_advarsel} advarsel</span>}
 {division.total_kpi_kritisk > 0 && <span style={{ fontSize: 12, color: C.kritisk }}>× {division.total_kpi_kritisk} kritisk</span>}
 </div> </div> <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}> <StatusDot status={divStatus} stor /> <span style={{ color: C.subtekst, fontSize: 14 }}>{aaben ? '▾' : '▸'}</span> </div> </div>
 {/* Afdelinger */}
 {aaben && (
 <div style={{
 border: `1px solid ${C.kant}`, borderTop: 'none',
 borderRadius: '0 0 10px 10px', overflow: 'hidden',
 }}>
 {division.afdelinger.map((afd, i) => {
 const kpier = afd.kpi_data || [];
 const erAktiv = aktivAfdeling?.kode === afd.kode;
 return (
 <div
 key={afd.kode}
 onClick={() => onVaelgAfdeling(afd)}
 style={{
 display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
 borderBottom: i < division.afdelinger.length - 1 ? `1px solid ${C.kant}` : 'none',
 cursor: 'pointer',
 background: erAktiv ? statusBaggrund(afd.status) : i % 2 === 0 ? 'rgba(255,255,255,0.03)' : C.kort,
 transition: 'background .1s',
 }}
 onMouseEnter={e => { if (!erAktiv) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
 onMouseLeave={e => { if (!erAktiv) e.currentTarget.style.background = i % 2 === 0 ? 'rgba(255,255,255,0.03)' : C.kort; }}
 > <StatusDot status={afd.status || 'ukendt'} /> <div style={{ flex: 1 }}> <span style={{ fontSize: 14, fontWeight: 600, color: C.tekst }}>{afd.navn}</span>
 {(() => {
 const medData = kpier.filter(k => k.vaerdi != null);
 if (medData.length > 0) {
 return (
 <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
 {medData.slice(0, 3).map(k => (
 <span key={k.kpi_navn} style={{
 fontSize: 10, color: statusFarve(k.status),
 background: statusBaggrund(k.status),
 padding: '1px 6px', borderRadius: 10,
 }}>
 {k.kpi_navn}: {k.vaerdi}{k.enhed ? ' ' + k.enhed : ''}
 </span>
 ))}
 </div>
 );
 }
 if (kpier.length > 0) {
 return (
 <div style={{ marginTop: 3 }}> <span style={{ fontSize: 10, color: '#475569', fontStyle: 'italic' }}>
 {kpier.length} KPI'er konfigureret — afventer målinger
 </span> </div>
 );
 }
 return null;
 })()}
 </div> <span style={{ fontSize: 12, color: C.subtekst }}>›</span> </div>
 );
 })}
 </div>
 )}
 </div>
 );
}
// ─── Scenarie progress bar ────────────────────────────────────────────────────
function ScenarieLinje({ navn, maerke, vaerdi, maal, farve }) {
 const pct = maal > 0 ? Math.min(100, Math.round((vaerdi / maal) * 100)) : 0;
 return (
 <div style={{ marginBottom: 18 }}> <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}> <div> <span style={{ fontSize: 13, fontWeight: 700, color: C.tekst }}>{navn}</span> <span style={{ fontSize: 11, color: C.subtekst, marginLeft: 8 }}>({maerke})</span> </div> <div style={{ textAlign: 'right' }}> <span style={{ fontSize: 13, fontWeight: 700, color: farve }}>{pct}%</span> <span style={{ fontSize: 11, color: C.subtekst, marginLeft: 6 }}>
 {(vaerdi / 1000).toFixed(0)}k / {(maal / 1000).toFixed(0)}k kr
 </span> </div> </div> <div style={{ height: 10, background: '#e2e8f0', borderRadius: 5, overflow: 'hidden' }}> <div style={{
 height: '100%', width: `${pct}%`, background: farve, borderRadius: 5,
 transition: 'width .6s ease', position: 'relative',
 }}>
 {pct > 15 && (
 <span style={{
 position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
 fontSize: 9, color: '#fff', fontWeight: 700,
 }}>{pct}%</span>
 )}
 </div> </div> </div>
 );
}
function GraferDashboard({ trend, leads, metrikker }) {
 const mrr = metrikker.find(m => m.metrik_kode === 'mrr');
 const mrrVaerdi = mrr?.vaerdi || 0;
 // Leads funnel til BarChart
 const kildeMap = {};
 for (const l of leads) {
 if (!kildeMap[l.kilde]) kildeMap[l.kilde] = { kilde: l.kilde, ny: 0, kontaktet: 0, møde: 0, klient: 0 };
 kildeMap[l.kilde][l.status] = l.antal || 0;
 }
 const leadsData = Object.values(kildeMap);
 const toCustomTick = (dateStr) => {
 if (!dateStr) return '';
 const d = new Date(dateStr);
 return `${d.getDate()}/${d.getMonth() + 1}`;
 };
 return (
 <div>
 {/* MRR Trend */}
 <Kort style={{ marginBottom: 20 }}> <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.tekst }}> MRR Trend — 12 uger</h3> <ResponsiveContainer width="100%" height={220}> <AreaChart data={trend} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}> <defs> <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1"> <stop offset="5%" stopColor={C.primary} stopOpacity={0.25} /> <stop offset="95%" stopColor={C.primary} stopOpacity={0.02} /> </linearGradient> </defs> <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" /> <XAxis dataKey="uge" tickFormatter={toCustomTick} tick={{ fontSize: 12, fill: C.subtekst }} /> <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: C.subtekst }} /> <Tooltip
 formatter={(v) => [`${v.toLocaleString('da-DK')} kr`, 'MRR']}
 labelFormatter={(l) => `Uge starten ${l}`}
 contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${C.kant}` }}
 /> <Area type="monotone" dataKey="mrr" stroke={C.primary} strokeWidth={2.5}
 fill="url(#mrrGradient)" dot={false} activeDot={{ r: 4, fill: C.primary }} /> </AreaChart> </ResponsiveContainer> </Kort>
 {/* Omsætningsscenarier 2026 */}
 <Kort style={{ marginBottom: 20 }}> <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.tekst }}> Omsætningsscenarier 2026 — Aktuel fremgang</h3> <div style={{ fontSize: 12, color: C.subtekst, marginBottom: 16 }}>
 Aktuel MRR annualiseret: <strong style={{ color: C.tekst }}>{((mrrVaerdi * 12) / 1000).toFixed(0)}k kr/år</strong> </div> <ScenarieLinje navn="Konservativt" maerke="2,5M kr" vaerdi={mrrVaerdi * 12} maal={2500000} farve="#64748b" /> <ScenarieLinje navn="Realistisk" maerke="3,5M kr" vaerdi={mrrVaerdi * 12} maal={3500000} farve={C.info} /> <ScenarieLinje navn="Ambitiøst" maerke="5M kr" vaerdi={mrrVaerdi * 12} maal={5000000} farve={C.accent} /> <div style={{ fontSize: 11, color: C.subtekst, marginTop: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
 Arbejdshypotese: Realistisk scenarie (3,5M). Forudsætter Facebook-gruppe aktiveret + SEO konverterer.
 </div> </Kort>
 {/* Aktive klienter + leads trend */}
 {trend.length > 0 && trend[0].aktive_klienter !== undefined && (
 <Kort style={{ marginBottom: 20 }}> <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.tekst }}> Aktive klienter + Leads — 12 uger</h3> <ResponsiveContainer width="100%" height={200}> <LineChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}> <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" /> <XAxis dataKey="uge" tickFormatter={toCustomTick} tick={{ fontSize: 12, fill: C.subtekst }} /> <YAxis tick={{ fontSize: 12, fill: C.subtekst }} /> <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${C.kant}` }} /> <Legend wrapperStyle={{ fontSize: 12 }} /> <Line type="monotone" dataKey="aktive_klienter" stroke={C.ok} strokeWidth={2}
 dot={false} name="Aktive klienter" /> <Line type="monotone" dataKey="nye_leads_maaned" stroke={C.advarsel} strokeWidth={2}
 dot={false} name="Nye leads" /> </LineChart> </ResponsiveContainer> </Kort>
 )}
 {/* Leads funnel */}
 {leadsData.length > 0 && (
 <Kort> <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.tekst }}> Leads Funnel — {new Date().toLocaleString('da-DK', { month: 'long' })}</h3> <ResponsiveContainer width="100%" height={200}> <BarChart data={leadsData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}> <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" /> <XAxis dataKey="kilde" tick={{ fontSize: 12, fill: C.subtekst }} /> <YAxis tick={{ fontSize: 12, fill: C.subtekst }} /> <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${C.kant}` }} /> <Legend wrapperStyle={{ fontSize: 12 }} /> <Bar dataKey="ny" name="Nye leads" fill={C.info} radius={[3,3,0,0]} /> <Bar dataKey="kontaktet" name="Kontaktet" fill={C.advarsel} radius={[3,3,0,0]} /> <Bar dataKey="møde" name="Møde" fill={C.lilla} radius={[3,3,0,0]} /> <Bar dataKey="klient" name="Klient" fill={C.ok} radius={[3,3,0,0]} /> </BarChart> </ResponsiveContainer> </Kort>
 )}
 {leadsData.length === 0 && (
 <Kort> <p style={{ color: C.subtekst, margin: 0, fontSize: 13 }}>
 Ingen leads-data for denne måned endnu.
 Brug <code>PATCH /api/os/firma/leads</code> til at tilføje data.
 </p> </Kort>
 )}
 </div>
 );
}
// ─── HOVED-KOMPONENT ─────────────────────────────────────────────────────────
export default function FirmaOverblik() {
 const [data, setData] = useState(null);
 const [divisioner, setDivisioner] = useState([]);
 const [leads, setLeads] = useState([]);
 const [trend, setTrend] = useState([]);
 const [loader, setLoader] = useState(true);
 const [hentFejl, setHentFejl] = useState(null);
 const [aktivAfdeling, setAktivAfdeling] = useState(null);
 const [visning, setVisning] = useState('overblik'); // overblik | divisioner | leads | grafer
 const hent = useCallback(async () => {
 setLoader(true);
 setHentFejl(null);
 try {
 const [o, d, l, t] = await Promise.allSettled([
 api('/os/firma/overblik'),
 api('/os/firma/divisioner'),
 api('/os/firma/leads'),
 api('/os/firma/trend?koder=mrr,aktive_klienter,nye_leads_maaned'),
 ]);
 if (o.status === 'fulfilled' && !o.value?.fejl) setData(o.value);
 else if (o.status === 'rejected') console.error('[FirmaOverblik] overblik:', o.reason);
 if (d.status === 'fulfilled' && !d.value?.fejl) setDivisioner(d.value?.divisioner || []);
 else if (d.status === 'rejected') console.error('[FirmaOverblik] divisioner:', d.reason);
 if (l.status === 'fulfilled' && !l.value?.fejl) setLeads(l.value?.leads || []);
 if (t.status === 'fulfilled' && !t.value?.fejl) setTrend(t.value?.trend || []);
 const allFailed = [o, d, l, t].every(r => r.status === 'rejected' || r.value?.fejl);
 if (allFailed) setHentFejl('Kunne ikke hente firmaOverblik — tjek forbindelsen');
 } catch (err) {
 console.error('[FirmaOverblik] hent fejl:', err);
 setHentFejl('Uventet fejl ved indlæsning');
 } finally {
 setLoader(false);
 }
 }, []);
 useEffect(() => { hent(); }, [hent]);
 // Samlede KPI-status
 const total_kritisk = divisioner.reduce((s, d) => s + d.total_kpi_kritisk, 0);
 const total_advarsel = divisioner.reduce((s, d) => s + d.total_kpi_advarsel, 0);
 const total_ok = divisioner.reduce((s, d) => s + d.total_kpi_ok, 0);
 const total_afd = divisioner.reduce((s, d) => s + d.afdelinger.length, 0);
 const firmStatus = total_kritisk > 0 ? 'kritisk' : total_advarsel > 0 ? 'advarsel' : total_ok > 0 ? 'ok' : 'ukendt';
 const TAB_KNAP = ({ id, label, ikon }) => (
 <button
 onClick={() => setVisning(id)}
 style={{
 background: visning === id ? C.primary : 'transparent',
 color: visning === id ? '#fff' : C.subtekst,
 border: `1px solid ${visning === id ? C.primary : C.kant}`,
 borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
 cursor: 'pointer', transition: 'all .15s',
 }}
 >
 {ikon} {label}
 </button>
 );
 if (loader) return (
 <div style={{ padding: 40, color: C.subtekst, textAlign: 'center' }}> <div style={{ fontSize: 24, marginBottom: 12 }}></div>
 Henter firmaOverblik…
 </div>
 );
 if (hentFejl) return (
 <div style={{ padding: 40, textAlign: 'center' }}> <div style={{ fontSize: 24, marginBottom: 12 }}></div> <div style={{ color: C.kritisk, fontWeight: 600, marginBottom: 12 }}>{hentFejl}</div> <button onClick={hent} style={{
 background: C.primary, color: '#fff', border: 'none', borderRadius: 8,
 padding: '8px 20px', fontSize: 14, cursor: 'pointer',
 }}>Prøv igen</button> </div>
 );
 return (
 <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
 {/* Header */}
 <div style={{
 background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
 padding: '24px 28px', borderRadius: '0 0 16px 16px', marginBottom: 24,
 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 }}> <div> <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: 22, fontWeight: 800 }}>
 SkilsmisseVejen — Firma Overblik
 </h2> <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
 {divisioner.length} divisioner · {total_afd} afdelinger · {new Date().toLocaleDateString('da-DK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
 </div> </div> <div style={{
 display: 'flex', alignItems: 'center', gap: 8,
 background: firmStatus === 'ok' ? 'rgba(34,197,94,0.15)' : firmStatus === 'kritisk' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
 border: `1px solid ${statusFarve(firmStatus)}`,
 borderRadius: 20, padding: '6px 16px',
 }}> <StatusDot status={firmStatus} stor /> <span style={{ color: statusFarve(firmStatus), fontSize: 13, fontWeight: 700 }}>
 {total_ok} OK · {total_advarsel} Advarsel · {total_kritisk} Kritisk
 </span> </div> </div>
 {/* Navigation */}
 <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}> <TAB_KNAP id="overblik" label="Overblik" ikon="BarChart2" /> <TAB_KNAP id="divisioner" label="Alle divisioner" ikon="" /> <TAB_KNAP id="leads" label="Leads Pipeline" ikon="Target" /> <TAB_KNAP id="grafer" label="Grafer &amp; Trends" ikon="TrendingUp" /> </div>
 {/* Overblik */}
 {visning === 'overblik' && (
 <div>
 {/* Virksomhedsmetrikker */}
 {data?.metrikker && <MetrikOverblik metrikker={data.metrikker} />}
 {/* KPI advarsler */}
 {data?.kpi_advarsler?.length > 0 && (
 <Kort style={{ marginBottom: 24, borderLeft: `4px solid ${C.kritisk}` }}> <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.tekst }}> KPI-advarsler der kræver opmærksomhed</h3> <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {data.kpi_advarsler.map((k, i) => (
 <div key={i} style={{
 display: 'flex', alignItems: 'center', gap: 10,
 padding: '8px 12px', borderRadius: 8,
 background: statusBaggrund(k.status),
 }}> <StatusDot status={k.status} /> <span style={{ fontSize: 13, color: C.tekst, flex: 1 }}> <strong>{k.afdeling_kode}</strong> — {k.kpi_navn}
 </span> <span style={{ fontSize: 13, fontWeight: 700, color: statusFarve(k.status) }}>
 {k.vaerdi} / mål {k.maal}
 </span> </div>
 ))}
 </div> </Kort>
 )}
 {/* Division-grid */}
 <h3 style={{ margin: '0 0 14px', fontSize: 16, color: C.tekst }}>Divisioner</h3> <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
 {divisioner.map(div => (
 <DivisionKort
 key={div.nr}
 division={div}
 aktiv={false}
 onClick={() => setVisning('divisioner')}
 />
 ))}
 </div>
 {/* Sager statistik */}
 {data?.sager && (
 <Kort> <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.tekst }}> Sagsportal</h3> <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}> <div> <div style={{ fontSize: 28, fontWeight: 800, color: C.info }}>{data.sager.aktive || 0}</div> <div style={{ fontSize: 12, color: C.subtekst }}>Aktive sager</div> </div> <div> <div style={{ fontSize: 28, fontWeight: 800, color: C.ok }}>{data.sager.nye_30d || 0}</div> <div style={{ fontSize: 12, color: C.subtekst }}>Nye (30 dage)</div> </div> <div> <div style={{ fontSize: 28, fontWeight: 800, color: C.subtekst }}>{data.sager.total || 0}</div> <div style={{ fontSize: 12, color: C.subtekst }}>Total</div> </div> </div> </Kort>
 )}
 </div>
 )}
 {/* Alle divisioner */}
 {visning === 'divisioner' && (
 <div> <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> <p style={{ margin: 0, fontSize: 14, color: C.subtekst }}>
 Klik på en division for at udfolde. Klik på en afdeling for at se detaljer og opdatere KPI'er.
 </p> <button onClick={hent} style={{
 background: C.info, color: '#fff', border: 'none', borderRadius: 8,
 padding: '6px 14px', fontSize: 13, cursor: 'pointer',
 }}> Opdater</button> </div>
 {divisioner.map(div => (
 <DivisionSektion
 key={div.nr}
 division={div}
 aktivAfdeling={aktivAfdeling}
 onVaelgAfdeling={setAktivAfdeling}
 />
 ))}
 </div>
 )}
 {/* Leads Pipeline */}
 {visning === 'leads' && (
 <div>
 {leads.length > 0 ? (
 <LeadsPipeline leads={leads} />
 ) : (
 <Kort> <p style={{ color: C.subtekst, margin: 0 }}>
 Ingen lead-data endnu. Opdater via API: <code>PATCH /api/os/firma/leads</code> </p> </Kort>
 )}
 <Kort style={{ marginTop: 16 }}> <h4 style={{ margin: '0 0 12px', fontSize: 14, color: C.tekst }}> Alle virksomhedsmetrikker</h4> <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
 {(data?.metrikker || []).map(m => {
 const kilde = m.datakilde || 'ikke_tilsluttet';
 const dimmed = kilde === 'ikke_tilsluttet';
 return (
 <div key={m.metrik_kode} style={{
 padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8,
 border: `1px solid ${C.kant}`, opacity: dimmed ? 0.5 : 1,
 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}> <div style={{ fontSize: 11, color: C.subtekst }}>{m.metrik_navn}</div>
 {kilde !== 'ikke_tilsluttet' && (
 <span style={{
 fontSize: 9, fontWeight: 700,
 color: kilde === 'live' ? C.ok : C.advarsel,
 background: kilde === 'live' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
 padding: '1px 5px', borderRadius: 6,
 }}>{kilde === 'live' ? '● Live' : 'Manuel'}</span>
 )}
 </div> <div style={{ fontSize: 18, fontWeight: 700, color: dimmed ? '#64748b' : C.tekst }}>
 {dimmed ? '—' : m.vaerdi != null ? (
 <>{m.vaerdi.toLocaleString('da-DK')}<span style={{ fontSize: 12, fontWeight: 400, color: C.subtekst, marginLeft: 4 }}>{m.enhed}</span></>
 ) : '—'}
 </div>
 {!dimmed && m.maal && <div style={{ fontSize: 11, color: C.subtekst }}>Mål: {m.maal} {m.enhed}</div>}
 </div>
 );
 })}
 </div> </Kort> </div>
 )}
 {/* Grafer & Trends */}
 {visning === 'grafer' && (
 <GraferDashboard
 trend={trend}
 leads={leads}
 metrikker={data?.metrikker || []}
 />
 )}
 {/* Afdeling detail-modal */}
 {aktivAfdeling && (
 <AfdelingDetail
 afdeling={aktivAfdeling}
 onLuk={() => setAktivAfdeling(null)}
 onRefresh={hent}
 />
 )}
 </div>
 );
}